import { Controller } from "@hotwired/stimulus";

/**
 * GO4S Design System — bascule de thème clair / sombre.
 *
 * Pose le thème sur <html data-theme="...">, le persiste dans localStorage,
 * et suit la préférence système tant que l'utilisateur n'a pas choisi.
 * Spec : docs/design-system/REFERENCE.md §1.3
 *
 * Usage :
 *   <button data-controller="theme" data-action="theme#toggle"
 *           data-theme-target="icon">…</button>
 */
export default class extends Controller {
  static targets = ["icon"];

  static STORAGE_KEY = "g4s-theme";

  connect() {
    this.media = window.matchMedia("(prefers-color-scheme: dark)");
    this.onSystemChange = this.onSystemChange.bind(this);
    this.media.addEventListener("change", this.onSystemChange);
    this.apply(this.current(), { persist: false });
  }

  disconnect() {
    if (this.media) this.media.removeEventListener("change", this.onSystemChange);
  }

  // Thème courant : choix utilisateur sinon préférence système.
  current() {
    const stored = localStorage.getItem(this.constructor.STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return this.media.matches ? "dark" : "light";
  }

  toggle() {
    const next = this.isDark() ? "light" : "dark";
    this.apply(next, { persist: true });
  }

  isDark() {
    return document.documentElement.dataset.theme === "dark";
  }

  apply(theme, { persist }) {
    document.documentElement.dataset.theme = theme;
    if (persist) localStorage.setItem(this.constructor.STORAGE_KEY, theme);
    this.renderIcon(theme);
  }

  // Si l'utilisateur n'a pas figé de choix, suivre le système.
  onSystemChange(e) {
    if (!localStorage.getItem(this.constructor.STORAGE_KEY)) {
      this.apply(e.matches ? "dark" : "light", { persist: false });
    }
  }

  renderIcon(theme) {
    if (!this.hasIconTarget) return;
    // Lucide : 'moon' en clair (action = passer sombre), 'sun' en sombre.
    this.iconTarget.dataset.lucide = theme === "dark" ? "sun" : "moon";
    // Re-rendre le SVG Lucide (l'icône a déjà été remplacée au chargement).
    window.g4sRenderIcons?.();
  }
}
